import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { type PatientImportJobStatus } from '@eluve/api-contract';
import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Heading,
  Stack,
  Text,
} from '@eluve/components';

import { StatusTag } from './components/StatusTag';
import {
  listPatientImportJobsAcrossTenantsQuery,
  listTenantsForPickerQuery,
} from './patient-import.operations';

type ImportJobRow = {
  id: string;
  tenantId: string;
  organization: string;
  filename: string;
  status: PatientImportJobStatus;
  totalRows: number | null;
  committedCount: number;
  failedCount: number;
  createdAt: string;
  updatedAt: string;
};

export const PatientImportsListPage: React.FC = () => {
  const { data: jobsData } = useSuspenseQuery(
    listPatientImportJobsAcrossTenantsQuery,
    { context: eluveAdminHasuraContext },
  );
  const { data: tenantsData } = useSuspenseQuery(listTenantsForPickerQuery, {
    context: eluveAdminHasuraContext,
  });

  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tenantsData.adminTenants ?? []) {
      if (t.id) map.set(t.id, t.name ?? '—');
    }
    return map;
  }, [tenantsData.adminTenants]);

  const rows: ImportJobRow[] = useMemo(
    () =>
      (jobsData.patientImportJobs ?? []).map((j) => ({
        id: j.id,
        tenantId: j.tenantId,
        organization: tenantNameById.get(j.tenantId) ?? '—',
        filename: j.filename,
        status: j.status as PatientImportJobStatus,
        totalRows: j.totalRows,
        committedCount: j.committedCount,
        failedCount: j.failedCount,
        createdAt: j.createdAt,
        updatedAt: j.updatedAt,
      })),
    [jobsData, tenantNameById],
  );

  const columns = useMemo(
    () =>
      new ColDefBuilder<ImportJobRow>()
        .linkSortable('filename', (row) => `/admin/patient-imports/${row.id}`)
        .defaultSortable('organization', 'Organization')
        .defaultSortable('status', {
          label: 'Status',
          cellRenderer: (row) => <StatusTag status={row.status} />,
        })
        .defaultSortable('totalRows', 'Total rows')
        .defaultSortable('committedCount', 'Committed')
        .defaultSortable('failedCount', 'Failed')
        .dateSortable('createdAt', 'Created')
        .build(),
    [],
  );

  return (
    <Stack gap={6} align="stretch" className="mx-auto max-w-7xl">
      <Stack direction="horizontal" align="center" justify="between">
        <Stack gap={1}>
          <Heading>Bulk Patient Import</Heading>
          <Text color="secondary">
            Upload a CSV to bulk import patients for an organization.
          </Text>
        </Stack>
        <Link to="/admin/patient-imports/new">
          <Button>New Import</Button>
        </Link>
      </Stack>
      <DataTable
        data={rows}
        columns={columns}
        enableGlobalSearch
        initialSortingState={[{ id: 'createdAt', desc: true }]}
      />
    </Stack>
  );
};
